// Widget de saldo de créditos do aluno + modal de recarga via Pix.
//
// MVP manual (2026-05-19): aluno paga Pix estático (mesma chave da
// DonationCard) com o email na mensagem do Pix. Admin recebe, confere
// e libera os créditos manualmente pela tela /manager/credits.
//
// Esse widget mostra apenas saldo + botão "Recarregar". O modal de recarga
// exibe QR + copia-e-cola + chave + instrução de mensagem.
//
// [[project-prepaid-credits-strategy]]

import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  Wallet,
  X
} from "lucide-react";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { generateQrWithLogoSvg } from "@app/ui/lib/qr-with-logo";

// Mesma chave Pix usada na DonationCard (estática, sem Pix Dinâmico ainda).
const PIX_COPIA_E_COLA =
  "00020126580014BR.GOV.BCB.PIX01366f2240b1-3355-429e-98f4-ba855374d45e5204000053039865802BR5925Luis Antonio Moraes Filho6009SAO PAULO62140510elgTV0ZmoZ630433CD";
const PIX_KEY = "6f2240b1-3355-429e-98f4-ba855374d45e";
const PIX_HOLDER = "Luis Antonio Moraes Filho";

interface CreditsState {
  balanceCents: number;
  totalToppedUpCents: number;
  totalConsumedCents: number;
}

export function CreditsWidget({ userEmail }: { userEmail: string | null }) {
  const [data, setData] = useState<CreditsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.getMyCredits();
      setData({
        balanceCents: r.balanceCents,
        totalToppedUpCents: r.totalToppedUpCents,
        totalConsumedCents: r.totalConsumedCents
      });
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  return (
    <section class="rounded-lg border border-border bg-card p-4">
      <header class="mb-2 flex items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 text-sm font-bold">
          <Wallet className="size-4 text-primary" />
          Meu saldo
        </h2>
        <button
          type="button"
          onClick={() => void load()}
          class="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
          title="Atualizar saldo"
          aria-label="Atualizar saldo"
        >
          {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
        </button>
      </header>

      {err && (
        <p class="mb-2 text-xs text-destructive">
          <AlertCircle className="mr-1 inline size-3" /> {err}
        </p>
      )}

      {data && (
        <>
          <div
            class={`text-2xl font-bold tabular-nums ${
              data.balanceCents < 100
                ? "text-amber-600 dark:text-amber-400"
                : "text-foreground"
            }`}
          >
            {formatBRL(data.balanceCents)}
          </div>
          <p class="mt-0.5 text-[11px] text-muted-foreground">
            {data.balanceCents < 100
              ? "Saldo baixo — recarregue para continuar usando a IA"
              : "Saldo disponível para consumo de IA"}
          </p>

          <div class="mt-3 grid grid-cols-2 gap-2 text-[10px]">
            <div class="rounded-md border border-border bg-background/60 px-2 py-1.5">
              <div class="font-semibold uppercase tracking-wider text-muted-foreground">
                Total recarregado
              </div>
              <div class="mt-0.5 font-bold tabular-nums">
                {formatBRL(data.totalToppedUpCents)}
              </div>
            </div>
            <div class="rounded-md border border-border bg-background/60 px-2 py-1.5">
              <div class="font-semibold uppercase tracking-wider text-muted-foreground">
                Total consumido
              </div>
              <div class="mt-0.5 font-bold tabular-nums">
                {formatBRL(data.totalConsumedCents)}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setModalOpen(true)}
            class="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground hover:brightness-110"
          >
            <Wallet className="size-4" />
            Recarregar saldo
          </button>
        </>
      )}

      {modalOpen && (
        <RechargeModal
          userEmail={userEmail}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}

function RechargeModal({
  userEmail,
  onClose
}: {
  userEmail: string | null;
  onClose: () => void;
}) {
  const [qrSvg, setQrSvg] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    void generateQrWithLogoSvg(PIX_COPIA_E_COLA, {
      margin: 1,
      dark: "#0f172a",
      light: "#ffffff"
    })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Recarregar saldo"
    >
      <div class="flex max-h-[90vh] w-full max-w-md flex-col overflow-y-auto rounded-xl border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Wallet className="size-5 text-primary" />
            Recarregar saldo
          </h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="space-y-4 p-5">
          <p class="text-xs text-muted-foreground">
            Pague via Pix qualquer valor que desejar. Os créditos vão para o seu saldo
            assim que o admin confirmar o recebimento.
          </p>

          {/* Aviso crítico: colocar o email na mensagem do Pix */}
          <div class="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            <div class="font-bold">Importante</div>
            <p class="mt-1">
              Coloque o seu email{" "}
              <span class="font-mono font-bold">{userEmail ?? "(seu email)"}</span> na{" "}
              <strong>mensagem do Pix</strong> para o admin identificar o pagamento.
              Sem o email a confirmação demora ou pode não acontecer.
            </p>
          </div>

          {/* QR Code */}
          {qrSvg ? (
            <div
              class="mx-auto h-48 w-48 overflow-hidden rounded-md bg-white p-2"
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
          ) : (
            <div class="mx-auto grid h-48 w-48 place-items-center rounded-md border border-border bg-muted/40">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}

          <CopyRow label="Pix copia e cola" value={PIX_COPIA_E_COLA} truncate />
          <CopyRow label="Chave Pix (aleatória)" value={PIX_KEY} />
          <CopyRow label="Beneficiário" value={PIX_HOLDER} />
          {userEmail && (
            <CopyRow label="Mensagem (cole exatamente assim)" value={userEmail} />
          )}

          <div class="rounded-md border border-border bg-background/60 px-3 py-2 text-[11px] text-muted-foreground">
            <strong class="text-foreground">Como funciona?</strong>
            <ol class="ml-4 mt-1 list-decimal space-y-0.5">
              <li>Você paga qualquer valor pelo Pix acima.</li>
              <li>Coloca seu email na mensagem do Pix.</li>
              <li>Admin recebe o Pix, confere e libera os créditos no seu saldo.</li>
              <li>Você pode usar IA até o saldo acabar.</li>
            </ol>
          </div>

          <a
            href="mailto:luis.moraesfh@gmail.com?subject=Recarga%20de%20cr%C3%A9ditos%20-%20Pix%20enviado&body=Olá%2C%20paguei%20a%20recarga%20pelo%20Pix.%20Pode%20confirmar%20o%20saldo%20no%20meu%20cadastro%3F"
            class="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold hover:bg-accent"
          >
            <ExternalLink className="size-4" />
            Avisar admin que paguei
          </a>
        </div>
      </div>
    </div>
  );
}

function CopyRow({
  label,
  value,
  truncate
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }
  return (
    <div class="space-y-1">
      <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div class="flex items-stretch gap-1">
        <code
          class={`flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs ${
            truncate ? "truncate" : "break-all"
          }`}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={() => void copy()}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 text-xs font-semibold hover:bg-accent"
        >
          {copied ? (
            <>
              <Check className="size-3 text-emerald-600 dark:text-emerald-400" />
              Copiado
            </>
          ) : (
            <>
              <Copy className="size-3" />
              Copiar
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}
